/**
 * Viewpoint planning module.
 * Resolves AI-authored viewpoint requests into either a concrete camera transform or
 * a documented no-op by searching, scoring, and selecting among fallback candidates.
 */
import type {
  ViewpointBounds,
  ViewpointPreset,
  ViewpointSceneContext,
  ViewpointToolData,
} from '../generate-jig.types'
import type { ViewpointQuaternion } from './quaternion'
import { lookAtToQuaternion } from './quaternion'
import type { ViewDecisionResult, ViewCandidate } from './view-decision-types'
import { buildViewCandidateLadder, getOutwardFamilyForTarget, getViewpointFamily } from './view-search-planner'
import { compareVisibilityMetrics, scoreCandidate, type VisibilityMetrics } from './visibility-scorer'
import { comparePreciseVisibility, scorePreciseVisibility, type PreciseVisibilityMetrics } from './precise-visibility-scorer'
import { resolveViewpointPolicy } from './viewpoint-policy'
import { isViewpointPreset } from './viewpoint-presets'

/**
 * Fully planned camera result returned by the viewpoint planner.
 * This extends the high-level decision metadata with the concrete transform that
 * can be written into a Jig stage.
 */
export interface PlannedViewpoint extends ViewDecisionResult {
  cameraPosition?: {
    x: number
    y: number
    z: number
  }
  targetPosition?: {
    x: number
    y: number
    z: number
  }
  rotation?: ViewpointQuaternion
  preciseMetrics?: PreciseVisibilityMetrics | null
}

interface CandidateEvaluation {
  candidate: ViewCandidate
  metrics: VisibilityMetrics
  preciseMetrics: PreciseVisibilityMetrics | null
}

function getResolvedPreset(tool: ViewpointToolData): ViewpointPreset {
  const preset = tool && tool.preset ? tool.preset : 'front'
  return isViewpointPreset(preset) ? preset : 'front'
}

function getValidDistance(value: any): number | undefined {
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) {
    return undefined
  }

  return value
}

function resolveBoundsByName(sceneContext: ViewpointSceneContext, elementName: string): ViewpointBounds | null {
  const searchName = elementName.trim().toLowerCase()

  if (!searchName) {
    return null
  }

  let exactMatch: ViewpointBounds | null = null
  sceneContext.elementBounds.forEach(function (bounds: ViewpointBounds, alias: string) {
    const loweredAlias = alias.toLowerCase()
    if (loweredAlias.indexOf('innerparent') >= 0) {
      return
    }

    if (loweredAlias === searchName) {
      exactMatch = bounds
    }
  })

  if (exactMatch) {
    return exactMatch
  }

  let partialMatch: ViewpointBounds | null = null
  sceneContext.elementBounds.forEach(function (bounds: ViewpointBounds, alias: string) {
    const loweredAlias = alias.toLowerCase()
    if (loweredAlias.indexOf('innerparent') >= 0) {
      return
    }

    if (!partialMatch && (loweredAlias.indexOf(searchName) >= 0 || searchName.indexOf(loweredAlias) >= 0)) {
      partialMatch = bounds
    }
  })

  return partialMatch
}

function toPlannedViewpoint(
  tool: ViewpointToolData,
  preset: ViewpointPreset,
  reasonCode: PlannedViewpoint['reasonCode'],
  candidate: ViewCandidate,
  note: string,
  usedFallback: boolean,
  preciseMetrics?: PreciseVisibilityMetrics | null
): PlannedViewpoint {
  return {
    capabilityMode: 'degraded-best-effort',
    reasonCode: reasonCode,
    requestedPreset: preset,
    selectedPreset: candidate.sourcePreset,
    selectedFamily: candidate.selectedFamily,
    directionLabel: candidate.directionLabel,
    targetName: tool.elementName ? String(tool.elementName) : undefined,
    targetPosition: candidate.targetPosition,
    cameraPosition: candidate.cameraPosition,
    distance: candidate.distance,
    rotation: lookAtToQuaternion(candidate.cameraPosition, candidate.targetPosition),
    degraded: usedFallback,
    usedFallback: usedFallback,
    note: note,
    preciseMetrics: typeof preciseMetrics === 'undefined' ? null : preciseMetrics,
  }
}

function createNoOpPlannedViewpoint(
  tool: ViewpointToolData,
  preset: ViewpointPreset,
  reasonCode: PlannedViewpoint['reasonCode'],
  note: string
): PlannedViewpoint {
  return {
    capabilityMode: 'no-op',
    reasonCode: reasonCode,
    requestedPreset: preset,
    selectedPreset: preset,
    selectedFamily: getViewpointFamily(preset),
    directionLabel: preset,
    targetName: tool.elementName ? String(tool.elementName) : undefined,
    degraded: true,
    usedFallback: false,
    note: note,
  }
}

function findTargetElementIds(sceneContext: ViewpointSceneContext, targetBounds: ViewpointBounds): Set<string> {
  return new Set(
    sceneContext.elements
      .filter(function (element) {
        return element.bounds === targetBounds
      })
      .map(function (element) {
        return element.id
      })
  )
}

function computeTargetEdgeStrength(sceneContext: ViewpointSceneContext, targetBounds: ViewpointBounds): number {
  const offsetX = targetBounds.center.x - sceneContext.sceneBounds.center.x
  const offsetZ = targetBounds.center.z - sceneContext.sceneBounds.center.z
  const halfSizeX = Math.max(sceneContext.sceneBounds.size.x / 2, 1e-6)
  const halfSizeZ = Math.max(sceneContext.sceneBounds.size.z / 2, 1e-6)

  return Math.max(Math.abs(offsetX) / halfSizeX, Math.abs(offsetZ) / halfSizeZ)
}

function evaluateCandidate(
  sceneContext: ViewpointSceneContext,
  targetBounds: ViewpointBounds,
  targetElementIds: Set<string>,
  tool: ViewpointToolData,
  candidate: ViewCandidate
): CandidateEvaluation {
  const preciseMetrics = sceneContext.visibilityContext
    ? scorePreciseVisibility({
      visibilityContext: sceneContext.visibilityContext,
      cameraPosition: candidate.cameraPosition,
      primaryTargetIds: Array.from(targetElementIds),
      contextTargetIds: [],
      occluderHiddenIds: new Set<string>(),
    })
    : null

  return {
    candidate: candidate,
    metrics: scoreCandidate(candidate, {
      sceneContext: sceneContext,
      targetBounds: targetBounds,
      targetElementIds: targetElementIds,
      requestedDirection: candidate.requestedDirection,
      policy: tool.policy,
    }),
    preciseMetrics: preciseMetrics,
  }
}

function findEvaluationByCandidate(evaluations: CandidateEvaluation[], candidate: ViewCandidate | null): CandidateEvaluation | null {
  if (!candidate) {
    return null
  }

  return evaluations.find(function (evaluation: CandidateEvaluation) {
    return evaluation.candidate === candidate
  }) || null
}

function isEvaluationDegraded(evaluation: CandidateEvaluation): boolean {
  return evaluation.metrics.quality === 'degraded' || (evaluation.preciseMetrics ? evaluation.preciseMetrics.quality === 'degraded' : false)
}

function compareEvaluations(candidate: CandidateEvaluation, currentBest: CandidateEvaluation): number {
  const preciseComparison = comparePreciseVisibility(candidate.preciseMetrics, currentBest.preciseMetrics)
  if (preciseComparison !== 0) {
    return preciseComparison
  }

  return compareVisibilityMetrics(candidate.metrics, currentBest.metrics, candidate.candidate.distance, currentBest.candidate.distance)
}

function selectBestEvaluation(evaluations: CandidateEvaluation[], allowDegraded: boolean): CandidateEvaluation | null {
  return evaluations.reduce<CandidateEvaluation | null>(function (best: CandidateEvaluation | null, evaluation: CandidateEvaluation) {
    if (!allowDegraded && isEvaluationDegraded(evaluation)) {
      return best
    }

    if (!best) {
      return evaluation
    }

    return compareEvaluations(evaluation, best) < 0 ? evaluation : best
  }, null)
}

function planSceneOverview(tool: ViewpointToolData, preset: ViewpointPreset, sceneContext: ViewpointSceneContext | null): PlannedViewpoint {
  if (sceneContext) {
    const candidate = buildViewCandidateLadder({
      sceneBounds: sceneContext.sceneBounds,
      targetBounds: sceneContext.sceneBounds,
      requestedPreset: preset,
      explicitDistance: getValidDistance(tool.distance),
      targetY: typeof tool.targetY === 'number' ? tool.targetY : 0,
      policy: tool.policy,
    })[0]

    return toPlannedViewpoint(tool, preset, 'requested_kept', candidate, 'scene overview path', false)
  }

  const candidate = buildViewCandidateLadder({
    sceneBounds: {
      center: { x: 0, y: 0, z: 0 },
      size: { x: 1, y: 1, z: 1 },
    },
    targetBounds: {
      center: { x: 0, y: 0, z: 0 },
      size: { x: 1, y: 1, z: 1 },
    },
    requestedPreset: preset,
    explicitDistance: getValidDistance(tool.distance),
    targetY: typeof tool.targetY === 'number' ? tool.targetY : 0,
    policy: tool.policy,
  })[0]

  return toPlannedViewpoint(tool, preset, 'requested_kept', candidate, 'scene overview legacy path', false)
}

/**
 * Resolves a requested viewpoint into either a concrete camera or a documented no-op.
 * The planner starts from the requested preset, evaluates a ladder of fallback candidates,
 * and then chooses the least-degraded option allowed by the active policy.
 */
export function planViewpoint(tool: ViewpointToolData, sceneContext: ViewpointSceneContext | null): PlannedViewpoint | null {
  if (!tool) {
    return null
  }

  const preset = getResolvedPreset(tool)
  const policy = resolveViewpointPolicy(tool.policy)

  if (!tool.elementName) {
    return planSceneOverview(tool, preset, sceneContext)
  }

  if (!sceneContext) {
    return createNoOpPlannedViewpoint(tool, preset, 'missing_scene_context', 'targeted viewpoint requires scene context')
  }

  const targetBounds = resolveBoundsByName(sceneContext, String(tool.elementName))
  if (!targetBounds) {
    return createNoOpPlannedViewpoint(tool, preset, 'ambiguous_target', 'could not resolve an exact target object')
  }

  const ladder = buildViewCandidateLadder({
    sceneBounds: sceneContext.sceneBounds,
    targetBounds: targetBounds,
    requestedPreset: preset,
    explicitDistance: getValidDistance(tool.distance),
    targetY: typeof tool.targetY === 'number' ? tool.targetY : 0,
    policy: tool.policy,
  })

  const requestedCandidate = ladder[0]
  const targetElementIds = findTargetElementIds(sceneContext, targetBounds)
  const evaluations = ladder.map(function (candidate: ViewCandidate) {
    return evaluateCandidate(sceneContext, targetBounds, targetElementIds, tool, candidate)
  })
  const requestedEvaluation = evaluations[0]
  const nudgeCandidate = ladder.find(function (candidate: ViewCandidate) {
    return candidate.sourceSeed === 'same-family-nudge'
  })
  const outwardFamily = getOutwardFamilyForTarget(sceneContext.sceneBounds, targetBounds, tool.policy)
  const outwardCandidate = outwardFamily
    ? ladder.find(function (candidate: ViewCandidate) {
      return candidate.sourceSeed === 'outward' && candidate.selectedFamily === outwardFamily
    })
    : null

  // Edge-biased targets often read better from the outward-facing side than from the
  // literal requested family, so allow that override before the generic fallback pass.
  if (
    policy.decision.preferEdgeOutwardOverride &&
    outwardCandidate &&
    outwardCandidate.selectedFamily !== getViewpointFamily(preset) &&
    requestedEvaluation &&
    computeTargetEdgeStrength(sceneContext, targetBounds) >= policy.decision.edgePresentationThreshold
  ) {
    const outwardEvaluation = findEvaluationByCandidate(evaluations, outwardCandidate)

    if (
      outwardEvaluation &&
      outwardEvaluation.metrics.quality === 'clear' &&
      outwardEvaluation.metrics.targetCoverage >= (requestedEvaluation.metrics.targetCoverage * policy.decision.edgePresentationMinCoverageRatio) &&
      outwardEvaluation.metrics.occluderOverlapRatio <= (requestedEvaluation.metrics.occluderOverlapRatio + policy.decision.edgePresentationMaxOverlapPenalty) &&
      outwardEvaluation.metrics.visibleSampleRatio + policy.decision.edgePresentationMaxVisibleRatioDrop >= requestedEvaluation.metrics.visibleSampleRatio
    ) {
      return toPlannedViewpoint(tool, preset, 'fallback_used', outwardCandidate, 'requested_edge_outward', true, outwardEvaluation.preciseMetrics)
    }
  }

  // Same-family nudges keep the presentation direction familiar while recovering
  // coverage when the literal requested framing cuts through the target.
  if (
    policy.decision.preferTopPresentationOverride &&
    nudgeCandidate &&
    requestedEvaluation &&
    requestedEvaluation.metrics.targetCoverage < policy.decision.topPresentationCoverageThreshold
  ) {
    const nudgeEvaluation = findEvaluationByCandidate(evaluations, nudgeCandidate)

    if (
      nudgeEvaluation &&
      nudgeEvaluation.metrics.targetCoverage >= (requestedEvaluation.metrics.targetCoverage * policy.decision.topPresentationGainThreshold)
    ) {
      return toPlannedViewpoint(tool, preset, 'fallback_used', nudgeCandidate, 'requested_same_family_nudge', true, nudgeEvaluation.preciseMetrics)
    }
  }

  // Keep the requested candidate when it is already serviceable; otherwise fall back
  // to the best non-requested option that survived visibility evaluation.
  if (
    requestedEvaluation &&
    requestedEvaluation.metrics.quality !== 'degraded' &&
    (!requestedEvaluation.preciseMetrics || requestedEvaluation.preciseMetrics.quality !== 'degraded')
  ) {
    return toPlannedViewpoint(tool, preset, 'requested_kept', requestedCandidate, 'requested_kept', false, requestedEvaluation.preciseMetrics)
  }

  const passingFallback = selectBestEvaluation(evaluations.slice(1), false)

  if (passingFallback) {
    return toPlannedViewpoint(tool, preset, 'fallback_used', passingFallback.candidate, 'requested_failed_visibility', true, passingFallback.preciseMetrics)
  }

  if (policy.decision.allowDegradedFallback) {
    const degradedFallback = selectBestEvaluation(evaluations.slice(1), true)

    if (degradedFallback) {
      return toPlannedViewpoint(tool, preset, 'fallback_used', degradedFallback.candidate, 'requested_failed_visibility', true, degradedFallback.preciseMetrics)
    }
  }

  return toPlannedViewpoint(
    tool,
    preset,
    'requested_kept',
    requestedCandidate,
    'requested_kept',
    false,
    requestedEvaluation ? requestedEvaluation.preciseMetrics : null
  )
}
